import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';

export interface SubscriptionInfo {
    paymentRecovery?: PaymentRecovery | null;
    status: string;
    cancelAtPeriodEnd?: boolean;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    nextPaymentDue: string | null;
    subscriptionId: string | null;
    customerId: string | null;
    failedAt?: string;
}

export interface PaymentRecovery {
    phase: string;
    nextRetryAt: string | null;
    suspendedAt: string | null;
    deletionAt: string | null;
}

interface PaymentState {
    subscription: SubscriptionInfo | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    cancelStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: PaymentState = {
    subscription: null,
    status: 'idle',
    cancelStatus: 'idle',
    error: null,
};

export const fetchSubscriptionInfo = createAsyncThunk(
    'payment/fetchSubscriptionInfo',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/v1/payment/subscription');
            return response.data as SubscriptionInfo;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

export const cancelSubscription = createAsyncThunk(
    'payment/cancelSubscription',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/v1/payment/subscription/cancel');
            return response.data;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

export const reactivateSubscription = createAsyncThunk(
    'payment/reactivateSubscription',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/v1/payment/subscription/reactivate');
            return response.data;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

export interface CardSetupIntent {
    clientSecret: string;
    publishableKey: string;
    setup_intent_id?: string;
    setup_intent_status?: string;
}

// Begin the in-app "Set up billing" flow: get a SetupIntent client_secret + publishable key for the
// Stripe Elements card form (mirrors the Keycloak signup card screen).
export const fetchSetupIntent = createAsyncThunk(
    'payment/fetchSetupIntent',
    async (options: {replace_card?: boolean} | undefined, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/v1/payment/setup-intent', options);
            return response.data as CardSetupIntent;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

// Finalize after the card is confirmed client-side: backend sets the default card, creates the metered
// subscription, and applies the account's server-owned signup policy exactly once.
// Legacy card accounts also supply their monthly budget with this request.

export const finalizeCard = createAsyncThunk(
    'payment/finalizeCard',
    async (payload: { setup_intent: string; customer_name?: string; customer_type?: 'individual' | 'business'; company_name?: string; budget_limit?: number }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/v1/payment/finalize-card', payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

export const fetchPortalSession = createAsyncThunk(
    'payment/fetchPortalSession',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/v1/payment/portal-session');
            return response.data.url as string;
        } catch (error) {
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const paymentSlice = createSlice({
    name: 'payment',
    initialState,
    reducers: {
        resetCancelStatus: (state) => {
            state.cancelStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscriptionInfo.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchSubscriptionInfo.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.subscription = action.payload;
            })
            .addCase(fetchSubscriptionInfo.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            })
            .addCase(cancelSubscription.pending, (state) => {
                state.cancelStatus = 'loading';
            })
            .addCase(cancelSubscription.fulfilled, (state) => {
                state.cancelStatus = 'succeeded';
            })
            .addCase(cancelSubscription.rejected, (state, action) => {
                state.cancelStatus = 'failed';
                state.error = action.payload as string;
            })
            .addCase(reactivateSubscription.pending, (state) => {
                state.cancelStatus = 'loading'; // reuse cancelStatus for now or add a new one
            })
            .addCase(reactivateSubscription.fulfilled, (state) => {
                state.cancelStatus = 'succeeded';
            })
            .addCase(reactivateSubscription.rejected, (state, action) => {
                state.cancelStatus = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { resetCancelStatus } = paymentSlice.actions;
export default paymentSlice.reducer;
